import { SlickPayError } from "./errors.js";

/**
 * Minimal fetch wrapper. Deliberately no axios: this package has to run in a
 * Vue CLI/webpack app, a Vite/React app and in Node without dragging a HTTP
 * client into any of them. `fetch` is native everywhere we support (browsers,
 * Node >= 18); a custom implementation can still be injected for tests or for
 * older Node.
 */
export async function request({
  baseUrl,
  path,
  method = "GET",
  token,
  query,
  body,
  timeout = 30000,
  fetchImpl,
  headers: extraHeaders,
}) {
  const doFetch = fetchImpl ?? globalThis.fetch;
  if (typeof doFetch !== "function") {
    throw new SlickPayError(
      "No fetch implementation available. Pass `fetch` in the SlickPay options (Node < 18).",
      { code: "config" }
    );
  }

  const url = new URL(String(path).replace(/^\//, ""), ensureTrailingSlash(baseUrl));
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
  }

  const controller = new AbortController();
  const timer = timeout > 0 ? setTimeout(() => controller.abort(), timeout) : null;

  let response;
  try {
    response = await doFetch(url.toString(), {
      method,
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        Authorization: `Bearer ${token}`,
        ...extraHeaders,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (cause) {
    const aborted = cause?.name === "AbortError";
    throw new SlickPayError(
      aborted
        ? `SlickPay request timed out after ${timeout}ms (${method} ${path})`
        : `SlickPay request failed (${method} ${path}): ${cause?.message ?? cause}`,
      { code: "network", cause }
    );
  } finally {
    if (timer) clearTimeout(timer);
  }

  const payload = await parseBody(response);

  if (!response.ok) {
    const error = new SlickPayError(
      `SlickPay responded ${response.status} for ${method} ${path}`,
      { status: response.status, body: payload, code: "http" }
    );
    const details = error.validationMessages;
    if (details.length) error.message += ` — ${details.join("; ")}`;
    throw error;
  }

  return payload;
}

async function parseBody(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function ensureTrailingSlash(value) {
  return value.endsWith("/") ? value : `${value}/`;
}
